<template>
  <div class="change-summary">
    <b-alert :variant="summaryVariant" show class="mb-2">
      <span class="font-weight-bold">{{ summaryTitle }}</span>
      <span v-if="isDryRun"></span>
      <span v-else-if="auditId !== null"> · 已寫入變更記錄（audit_id={{ auditId }}）</span>
    </b-alert>

    <h6 class="mb-1">欄位變更（{{ changes.length }} 項）</h6>
    <b-table
      v-if="changes.length > 0"
      bordered
      small
      striped
      :items="changes"
      :fields="changeFields"
      class="mb-3"
    >
      <template #cell(field)="row">
        {{ fieldLabel(row.item.field) }}
      </template>
      <template #cell(before)="row">
        <span class="text-danger">{{ displayValue(row.item.before) }}</span>
      </template>
      <template #cell(after)="row">
        <span class="text-success">{{ displayValue(row.item.after) }}</span>
      </template>
    </b-table>
    <p v-else class="text-muted">後端未回傳欄位變更（changes 為空）。</p>

    <h6 class="mb-1">連帶影響</h6>
    <b-table
      bordered
      small
      striped
      :items="impactRows"
      :fields="impactFields"
      class="mb-3"
    />

    <h6 class="mb-1">重定向映射</h6>
    <p :class="mappingCreated === null ? 'text-muted' : ''">{{ mappingText }}</p>

    <h6 class="mb-1">後端說明（notes）</h6>
    <ul v-if="notes.length > 0">
      <li v-for="(note, index) in notes" :key="index">{{ note }}</li>
    </ul>
    <p v-else class="text-muted">後端未回傳 notes。</p>
  </div>
</template>

<script>
import { formatNumber, normalizeNotes } from '@/utils/posId'

/** changes[].field 是後端機器欄位名，這裡只加中文說明，值一律用後端回傳的 */
const FIELD_LABELS = {
  pos_id: 'pos-id',
  pos_id_exact: 'pos-id（精確）',
  film_id: '影片 id',
  master_id: 'master id',
  film_master_id: 'master id',
  title_name: '台帳標題',
  title_source: '標題來源',
  status: '狀態',
  manual_linked: '手工鎖定',
  remarks: '備註'
}

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function toDisplay(value) {
  if (!hasText(value)) {
    return '（空）'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

function rangeText(from, to) {
  if (!hasText(from) && !hasText(to)) {
    return '不限（全部日期）'
  }
  const start = hasText(from) ? String(from) : '不限'
  const end = hasText(to) ? String(to) : '不限'
  return `${start} ~ ${end}`
}

/**
 * 渲染 PUT /api/film-circuit-ids/{id} 的回應（dry_run 與正式提交共用同一份渲染）。
 * 契約：guide §2.3 響應；notes[] 逐條原樣展示，不自行拼裝金額文案。
 */
export default {
  name: 'ChangeSummary',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  computed: {
    changes() {
      return this.data && Array.isArray(this.data.changes) ? this.data.changes : []
    },
    titleSync() {
      return this.data && this.data.title_sync ? this.data.title_sync : null
    },
    carryOver() {
      return this.data && this.data.carry_over ? this.data.carry_over : null
    },
    mappingCreated() {
      const mapping = this.data ? this.data.mapping_created : null
      return mapping === undefined ? null : mapping
    },
    auditId() {
      const id = this.data ? this.data.audit_id : null
      return id === undefined ? null : id
    },
    isDryRun() {
      return Boolean(this.data) && this.data.dry_run === true
    },
    notes() {
      return normalizeNotes(this.data ? this.data.notes : null)
    },
    summaryVariant() {
      return this.isDryRun ? 'info' : 'success'
    },
    summaryTitle() {
      return this.isDryRun ? '預覽結果' : '提交完成'
    },
    mappingText() {
      const mapping = this.mappingCreated
      if (!mapping) {
        return '未寫入重定向映射'
      }
      return `已寫入映射：${mapping.from_pos_id} → ${mapping.to_pos_id}（id: ${mapping.id}）`
    },
    impactRows() {
      const rows = []
      const carry = this.carryOver
      const sync = this.titleSync
      const carryEnabled = Boolean(carry) && carry.mode === 'engagements'
      const syncEnabled = Boolean(sync) && sync.enabled === true

      let carryValue = '後端未回傳'
      if (carry) {
        carryValue = carryEnabled ? '連帶遷移' : '不遷移'
      }
      rows.push({ item: '遷移關聯在記錄的票房', value: carryValue })

      // 「已回寫日結 / 每小時」= 本次操作實際影響的歷史記錄條數（dry_run 也會計算）：
      //  - 有 carry_over 改掛：取 carry_over.finals_updated / hourlies_updated
      //  - 只有 title_sync 標題回寫：取 title_sync.finals_updated / hourlies_updated
      //  - 兩者同時啟用：取兩者最大值（同一批記錄只算一次，避免 carry_over 已處理時 title_sync 回 0）
      let finalsUpdated = null
      let hourliesUpdated = null
      if (carryEnabled && syncEnabled) {
        finalsUpdated = Math.max(Number(carry.finals_updated) || 0, Number(sync.finals_updated) || 0)
        hourliesUpdated = Math.max(Number(carry.hourlies_updated) || 0, Number(sync.hourlies_updated) || 0)
      } else if (carryEnabled) {
        finalsUpdated = Number(carry.finals_updated) || 0
        hourliesUpdated = Number(carry.hourlies_updated) || 0
      } else if (syncEnabled) {
        finalsUpdated = Number(sync.finals_updated) || 0
        hourliesUpdated = Number(sync.hourlies_updated) || 0
      }
      if (finalsUpdated === null) {
        rows.push({ item: '已回寫日結記錄（finals_updated）', value: '—（未啟用回寫 / 遷移）' })
        rows.push({ item: '已回寫每小時記錄（hourlies_updated）', value: '—（未啟用回寫 / 遷移）' })
      } else {
        rows.push({ item: '已回寫日結記錄（finals_updated）', value: formatNumber(finalsUpdated) })
        rows.push({ item: '已回寫每小時記錄（hourlies_updated）', value: formatNumber(hourliesUpdated) })
      }

      if (carryEnabled) {
        rows.push({ item: '遷移金額（gross）', value: formatNumber(carry.gross) })
        rows.push({ item: '遷移人次（admissions）', value: formatNumber(carry.admissions) })
        rows.push({ item: '遷移涉及日期範圍', value: rangeText(carry.date_from, carry.date_to) })
      }

      if (sync) {
        let syncValue = '否（未勾選「回寫標注的影片標題」，歷史票房標題不變）'
        if (syncEnabled) {
          syncValue = '是（連帶回寫歷史票房標題）'
        }
        rows.push({ item: '標題回寫歷史（title_sync.enabled）', value: syncValue })
      } else {
        rows.push({ item: '標題回寫歷史（title_sync）', value: '後端未回傳' })
      }

      return rows
    },
    changeFields() {
      return [
        { key: 'field', label: '欄位' },
        { key: 'before', label: '變更前' },
        { key: 'after', label: '變更後' }
      ]
    },
    impactFields() {
      return [
        { key: 'item', label: '項目' },
        { key: 'value', label: '數值' }
      ]
    }
  },
  methods: {
    fieldLabel(field) {
      const label = FIELD_LABELS[field]
      return label ? `${label}（${field}）` : String(field)
    },
    displayValue: toDisplay
  }
}
</script>
